import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Loader2,
  Search,
  Check,
  ClipboardList,
  RefreshCcw,
  FileDown,
  X,
  Upload,
  FileText,
} from "lucide-react";
import { api, formatApiErrorDetail, API } from "@/lib/api";

const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "submitted", label: "Submitted" },
  { id: "pending", label: "Pending" },
];

export default function IntakeFormsManager() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [showUpload, setShowUpload] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/intake-forms");
      setForms(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let out = forms;
    if (tab !== "all") out = out.filter((f) => f.status === tab);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter(
        (f) =>
          (f.client_name || "").toLowerCase().includes(needle) ||
          (f.client_email || "").toLowerCase().includes(needle)
      );
    }
    return out;
  }, [forms, tab, q]);

  const counts = useMemo(() => ({
    all: forms.length,
    submitted: forms.filter((f) => f.status === "submitted").length,
    pending: forms.filter((f) => f.status === "pending").length,
  }), [forms]);

  const resend = async (id) => {
    if (!window.confirm("Resend the intake form email to this client?")) return;
    try {
      await api.post(`/admin/intake-forms/${id}/resend`);
      toast.success("Invitation resent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteForm = async (id) => {
    if (!window.confirm("Delete this intake form record? This can't be undone.")) return;
    try {
      await api.delete(`/admin/intake-forms/${id}`);
      toast.success("Intake form deleted");
      setSelectedId(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (selectedId) {
    const form = forms.find((f) => f.id === selectedId);
    return <IntakeFormDetail form={form} onBack={() => setSelectedId(null)} onResend={resend} onDelete={deleteForm} />;
  }

  return (
    <div data-testid="intake-forms-workspace">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-1">Intake forms</div>
          <h2 className="font-serif text-3xl text-ink">Every welcome &amp; agreement, in one place.</h2>
          <p className="text-sm text-ink-soft mt-1">{counts.all} total · {counts.submitted} submitted · {counts.pending} pending.</p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          data-testid="upload-intake-button"
          className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-medium hover:bg-navy-deep transition-colors"
        >
          <Upload size={14} /> Upload paper / PDF copy
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="inline-flex rounded-full bg-cream border border-sand/80 p-1">
          {STATUS_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`intake-tab-${t.id}`}
              className={`px-4 py-1.5 text-xs uppercase tracking-[0.18em] rounded-full transition-all ${tab === t.id ? "bg-navy text-cream" : "text-ink-soft hover:text-ink"}`}
            >
              {t.label} · {counts[t.id]}
            </button>
          ))}
        </div>
        <div className="relative flex-grow max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            data-testid="intake-search"
            className="w-full bg-cream border border-sand/70 rounded-full pl-9 pr-4 py-2 text-sm text-ink focus:outline-none focus:border-navy"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex items-center justify-center text-ink-muted">
          <Loader2 size={20} className="animate-spin mr-2" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-ink-soft italic border border-dashed border-sand rounded-2xl">
          No intake forms match this view yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="intake-list">
          {filtered.map((f) => (
            <button
              key={f.id}
              onClick={() => setSelectedId(f.id)}
              data-testid={`intake-row-${f.id}`}
              className="w-full text-left bg-cream-soft border border-sand/70 rounded-2xl px-5 py-4 hover:border-navy/40 transition-colors flex flex-wrap items-center justify-between gap-3"
            >
              <div className="flex items-center gap-4 min-w-0 flex-grow">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${f.kind === "manual" ? "bg-navy/10 text-navy" : "bg-cream border border-sand text-ink-soft"}`}>
                  {f.kind === "manual" ? <FileText size={16} /> : <ClipboardList size={16} />}
                </div>
                <div className="min-w-0">
                  <div className="font-serif text-lg text-ink truncate">{f.client_name || f.client_email}</div>
                  <div className="text-xs text-ink-soft truncate">{f.client_email} · {format(new Date(f.created_at), "d MMM yyyy")}{f.kind === "manual" ? " · uploaded" : ""}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {f.status === "submitted" ? (
                  <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-navy bg-navy/10 px-3 py-1 rounded-full">
                    <Check size={11} /> Submitted
                  </span>
                ) : (
                  <span className="text-[11px] uppercase tracking-[0.18em] text-ink-soft bg-cream border border-sand px-3 py-1 rounded-full">Pending</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {showUpload && (
        <UploadIntakeModal
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); load(); }}
        />
      )}
    </div>
  );
}

function IntakeFormDetail({ form, onBack, onResend, onDelete }) {
  if (!form) return null;
  const token = localStorage.getItem("ams_token");
  const fileUrl = form.kind === "manual"
    ? `${API}/admin/intake-forms/${form.id}/file?auth=${encodeURIComponent(token || "")}`
    : null;
  return (
    <div data-testid="intake-form-detail">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-navy mb-6">
        ← Back to all forms
      </button>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-1">Intake form</div>
          <h2 className="font-serif text-3xl text-ink">{form.client_name || form.client_email}</h2>
          <p className="text-sm text-ink-soft mt-1">{form.client_email} · {format(new Date(form.created_at), "EEE d MMM yyyy")}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {form.status === "pending" && (
            <button
              onClick={() => onResend(form.id)}
              className="inline-flex items-center gap-1.5 rounded-full bg-cream text-navy border border-navy/30 hover:bg-navy hover:text-cream px-4 py-2 text-xs font-medium transition-colors"
            >
              <RefreshCcw size={12} /> Resend email
            </button>
          )}
          <button
            onClick={() => onDelete(form.id)}
            data-testid="intake-delete-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-cream text-red-700 border border-red-300/60 hover:bg-red-700 hover:text-cream px-4 py-2 text-xs font-medium transition-colors"
          >
            <X size={12} /> Delete
          </button>
          {fileUrl && (
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="intake-open-file"
              className="inline-flex items-center gap-1.5 rounded-full bg-navy text-cream px-4 py-2 text-xs font-medium hover:bg-navy-deep transition-colors"
            >
              <FileDown size={12} /> Open uploaded file
            </a>
          )}
        </div>
      </div>

      {form.kind === "manual" ? (
        <div className="bg-cream-soft border border-sand/70 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <FileText size={18} className="text-navy" />
            <div>
              <div className="text-sm text-ink font-medium">{form.attachment?.original_filename}</div>
              <div className="text-xs text-ink-soft">
                {form.attachment?.content_type}
                {form.attachment?.size ? ` · ${(form.attachment.size / 1024).toFixed(1)} KB` : ""}
              </div>
            </div>
          </div>
          {form.admin_notes && (
            <div className="mt-4 p-4 bg-cream rounded-xl text-sm text-ink-muted border border-sand/50">
              <div className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-1">Notes</div>
              {form.admin_notes}
            </div>
          )}
        </div>
      ) : form.status === "submitted" ? (
        <WebIntakeView form={form} />
      ) : (
        <div className="bg-cream-soft border border-dashed border-sand rounded-2xl p-8 text-center text-ink-soft italic">
          This client hasn't filled in their forms yet. The invitation link is still active.
        </div>
      )}
    </div>
  );
}

function WebIntakeView({ form }) {
  const i = form.intake || {};
  const a = form.agreement || {};
  const ec = i.emergency_contact || {};
  const Row = ({ label, value }) => (
    <div className="py-3 border-b border-sand/30 last:border-0">
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft mb-1">{label}</div>
      <div className="text-sm text-ink whitespace-pre-wrap">{value || <span className="text-ink-soft italic">—</span>}</div>
    </div>
  );
  return (
    <div className="bg-cream-soft border border-sand/70 rounded-2xl p-6">
      <h3 className="font-serif text-lg text-ink mb-3">Part 1 — Personal details</h3>
      <div className="grid sm:grid-cols-2 gap-x-6">
        <Row label="Full legal name" value={i.full_legal_name} />
        <Row label="Preferred name" value={i.preferred_name} />
        <Row label="Date of birth" value={i.date_of_birth} />
        <Row label="Phone" value={i.phone} />
        <Row label="Email" value={i.email} />
        <Row label="Occupation" value={i.occupation} />
        <Row label="Address" value={i.address} />
        <Row label="Emergency contact" value={`${ec.name || ""}${ec.relationship ? " (" + ec.relationship + ")" : ""}${ec.phone ? " · " + ec.phone : ""}`} />
      </div>

      <h3 className="font-serif text-lg text-ink mt-6 mb-3">Part 2 — Concerns &amp; goals</h3>
      <Row label="Primary reasons for counselling" value={i.primary_reasons} />
      <Row label="Duration of difficulties" value={i.duration} />
      <Row label="Goals for our work together" value={i.goals} />
      <Row label="Current distress level (1–10)" value={String(i.distress_level || "")} />

      <h3 className="font-serif text-lg text-ink mt-6 mb-3">Part 3 — Health &amp; wellness</h3>
      <Row label="Mental health diagnoses" value={i.mental_health_history} />
      <Row label="Prior therapy" value={i.prior_therapy} />
      <Row label="Medications" value={i.medications} />
      <Row label="Other healthcare professionals" value={i.other_treatment} />
      <Row label="Physical health" value={i.physical_health} />
      <Row label="Alcohol / recreational drug use" value={i.substance_use} />

      <div className="mt-6 pt-4 border-t border-sand/40">
        <div className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-2">Counselling agreement</div>
        <div className="text-sm text-ink">
          Signed by <strong className="font-serif italic text-base">{a.typed_signature}</strong> on {a.signed_on}
        </div>
        <div className="text-xs text-ink-soft mt-1">All three agreement clauses ticked (Collaboration · Process · Confidentiality)</div>
      </div>
    </div>
  );
}

function UploadIntakeModal({ onClose, onUploaded }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!clientId || !file) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(
        `/admin/intake-forms/upload?client_id=${encodeURIComponent(clientId)}&notes=${encodeURIComponent(notes)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Form uploaded");
      onUploaded();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-50 flex items-start justify-center p-6 overflow-y-auto" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="upload-intake-form"
        className="bg-cream-soft border border-sand/70 rounded-3xl p-7 w-full max-w-lg mt-12 shadow-2xl"
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-ink-soft">New intake</div>
            <h3 className="font-serif text-2xl text-ink">Upload a paper or PDF copy</h3>
          </div>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>
        <p className="text-sm text-ink-soft mb-5">For clients who returned a printed copy or emailed back a PDF. The file is stored against their client profile and appears in this Intake forms list.</p>

        <div className="space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-1.5 block">Client</label>
            <select
              required
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              data-testid="upload-client-select"
              className="w-full bg-cream border border-sand rounded-xl px-3.5 py-3 text-ink text-sm focus:outline-none focus:border-navy"
            >
              <option value="">— pick the client —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name || c.email}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-1.5 block">File (PDF, JPG or PNG, max 15MB)</label>
            <input
              required
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,application/pdf,image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="upload-file-input"
              className="w-full bg-cream border border-sand rounded-xl px-3.5 py-3 text-ink text-sm file:mr-3 file:rounded-full file:border-0 file:bg-navy file:text-cream file:px-3 file:py-1.5 file:text-xs file:cursor-pointer"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-1.5 block">Notes (optional)</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. 'Returned hard copy at first session, 2 pages.'"
              className="w-full bg-cream border border-sand rounded-xl px-3.5 py-3 text-ink text-sm focus:outline-none focus:border-navy resize-y"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={saving || !clientId || !file}
          data-testid="upload-submit"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-cream px-6 py-3 text-sm font-medium hover:bg-navy-deep transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload form
        </button>
      </form>
    </div>
  );
}
